import { useCallback, useEffect, useState } from 'react';
import Employee from '../../../../../services/Employee';
import HireDocuments from '../../../../../services/HireDocuments';
import HireApplication from '../../../../../services/HireApplication';
import BankingAccountsService from '../../../../../services/BankingAccounts';
import EmploymentDocumentsService from '../../../../../services/EmploymentDocuments';
import { SignedDocSummary, ValidationTokenResponse } from '../types';

interface UseDocumentsFlowResult {
  isLoading: boolean;
  isValidToken: boolean;
  tokenData: ValidationTokenResponse | null;
  signedDocs: SignedDocSummary[];
  draftDocs: SignedDocSummary[];
  application: any;
  applicationDraft: any;
  bankingAccounts: any[];
  uploadedDocuments: any[];
  refresh: () => Promise<void>;
}

const toSummary = (doc: any): SignedDocSummary => ({
  type: doc?.type,
  id: doc?.id,
  signatureDate: doc?.options?.signatureDate || doc?.updatedAt,
  uploadedPdf: doc?.uploadedPdf || null,
  options: doc?.options,
});

const extractDocs = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const useDocumentsFlow = (token: string): UseDocumentsFlowResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [isValidToken, setIsValidToken] = useState(true);
  const [tokenData, setTokenData] = useState<ValidationTokenResponse | null>(null);
  const [signedDocs, setSignedDocs] = useState<SignedDocSummary[]>([]);
  const [draftDocs, setDraftDocs] = useState<SignedDocSummary[]>([]);
  const [application, setApplication] = useState<any>(null);
  const [applicationDraft, setApplicationDraft] = useState<any>(null);
  const [bankingAccounts, setBankingAccounts] = useState<any[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([]);

  const load = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const tokenResponse = await Employee('').documentsValidationToken(token);
      const data: ValidationTokenResponse = tokenResponse?.data;
      setTokenData(data);
      setIsValidToken(true);

      if (data?.userId) {
        const [docsList, applicationList, banks, uploaded] = await Promise.all([
          HireDocuments('')
            .show(data.userId, { includeDrafts: true, userToken: token })
            .catch(() => []),
          HireApplication('')
            .show(data.userId, { includeDrafts: true, userToken: token })
            .catch(() => null),
          data.employeeId
            ? BankingAccountsService(token)
                .list(data.employeeId)
                .catch(() => [])
            : Promise.resolve([]),
          data.employeeId
            ? EmploymentDocumentsService(token)
                .list(data.employeeId)
                .catch(() => [])
            : Promise.resolve([]),
        ]);

        const docs = extractDocs(docsList);
        setSignedDocs(docs.filter((d: any) => !d.isDraft).map(toSummary));
        setDraftDocs(docs.filter((d: any) => d.isDraft).map(toSummary));

        const rawApplication = applicationList?.data || applicationList || null;
        const applicationData = rawApplication?.id ? rawApplication : null;
        if (applicationData?.isDraft) {
          setApplicationDraft(applicationData);
          setApplication(null);
        } else {
          setApplication(applicationData);
          setApplicationDraft(null);
        }

        setBankingAccounts(Array.isArray(banks) ? banks : banks?.data || []);
        setUploadedDocuments(Array.isArray(uploaded) ? uploaded : uploaded?.data || []);
      }
    } catch {
      setIsValidToken(false);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    isLoading,
    isValidToken,
    tokenData,
    signedDocs,
    draftDocs,
    application,
    applicationDraft,
    bankingAccounts,
    uploadedDocuments,
    refresh: load,
  };
};

export default useDocumentsFlow;
